import {
  ActivatedRouteSnapshot,
  Router,
  type CanActivateFn,
} from '@angular/router';
import { inject } from '@angular/core';
import { MenuService } from '../services/menu.service';
import {
  MsalService,
  MsalGuardConfiguration,
  MSAL_GUARD_CONFIG,
} from '@azure/msal-angular';
import { map, Observable, of } from 'rxjs';

export const authGuard: CanActivateFn = (route) => {
  const router = inject(Router);
  const menu = inject(MenuService);
  menu.selectedMenu(route.routeConfig?.path ?? '');

  const msalService = inject(MsalService);
  const msalGuardConfig = inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG);

  const msalGuard = new (class {
    constructor(
      public config: MsalGuardConfiguration,
      public msalService: MsalService,
    ) {}

    canActivate(): Observable<boolean> {
      const account = this.msalService.instance.getActiveAccount();
      if (!account) {
        return of(false);
      }
      return of(true);
    }
  })(msalGuardConfig, msalService);

  return msalGuard.canActivate().pipe(
    map((canActivate) => {
      if (!canActivate) {
        return false;
      }

      const account = msalService.instance.getActiveAccount();
      if (!account) {
        return false;
      }

      const userRoles = account.idTokenClaims?.roles || [];
      const requiredRoles = getRequiredRoles(route);

      if (!requiredRoles || requiredRoles.length === 0) {
        return true;
      }

      const hasRequiredRole = requiredRoles.some((role) =>
        userRoles.includes(role),
      );
      if (!hasRequiredRole) {
        return router.parseUrl('/forbidden');
      }

      return true;
    }),
  );
};

function getRequiredRoles(route: ActivatedRouteSnapshot): string[] {
  let roles: string[] = [];

  if (route.data?.['roles']) {
    roles = [...roles, ...route.data['roles']];
  }

  let parent = route.parent;
  while (parent) {
    if (parent.data?.['roles']) {
      roles = [...roles, ...parent.data['roles']];
    }
    parent = parent.parent;
  }

  return [...new Set(roles)];
}
